/* eslint-disable confluence-feature-gating/no-preconditioning */
import React, { useCallback, useMemo, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { extractContentFeatures } from '@atlassian/conversation-assistant';
import {
	ExperienceName as RovoExperienceName,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { useEditorRovoBridge } from '@atlassian/editor-rovo-bridge/use-editor-rovo-bridge';
import type { AddCommandHandlersFn } from '@atlassian/editor-rovo-bridge/command-manager';
import type { AdditionalContext } from '@atlassian/editor-rovo-bridge/content';
import type {
	CreateMediaProviderFn,
	UploadImageErrorCallback,
} from '@atlassian/editor-rovo-bridge/media';
import type {
	StreamAbortCallback,
	StreamCallback,
	StreamEndCallback,
	StreamErrorCallback,
	StreamRepairCallback,
	StreamStartCallback,
	StreamTimeoutCallback,
} from '@atlassian/editor-rovo-bridge/command-handler';
import {
	ConfluenceDeepResearchPageCreateCommand,
	ConfluencePageInitCommand,
	ConfluenceSubmitPromptOnPageCreateCommand,
	PrepareContentForFinalizationCommand,
} from '@atlassian/rovo-content-bridge-api-commands';
import { sendAIFCExperienceTrackingToParent } from '@atlassian/rovo-content-bridge-api-experience-tracking-sender';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store';

import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider.ts';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLazyShowFeedbackFlag } from '@confluence/smart-create/entry-points/useLazyShowFeedbackFlag.ts';
import { FeedbackVariant } from '@confluence/smart-create/entry-points/FeedbackVariant.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { DeepResearchCommandHandler } from '../command-handlers-new/deep-research-command-handler.ts';
import { PageInitCommandHandler } from '../command-handlers-new/page-init-command-handler.ts';
import { PrepareContentForFinalizationCommandHandler } from '../command-handlers-new/prepare-content-for-finalization-command-handler.ts';
import { SubmitPromptCommandHandler } from '../command-handlers-new/submit-prompt-command-handler.ts';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener.tsx';
import { log } from '../utils/aifc-logging.ts';
import { getDocument } from '../utils/bridge-document-utils.ts';
import { useIsPageViewMode } from '../utils/useIsPageViewMode.ts';

import { useStreamedContentRetentionMetric } from './useStreamedContentRetentionMetric.ts';

const i18nMessages = defineMessages({
	uploadErrorTitle: {
		id: 'rovo-editor-bridge-listener.image-upload-error.title',
		defaultMessage: 'An error occurred',
		description: 'Title for the error flag shown when an AI-generated image upload fails',
	},
	uploadErrorDescription: {
		id: 'rovo-editor-bridge-listener.image-upload-error.description',
		defaultMessage: "We weren't able to add your infographic.",
		description: 'Description for the error flag shown when an AI-generated image upload fails',
	},
	uploadErrorTryAgain: {
		id: 'rovo-editor-bridge-listener.image-upload-error.try-again',
		defaultMessage: 'Try again',
		description: 'Button label to retry a failed image upload',
	},
	previewModeBlockedTitle: {
		id: 'rovo-editor-bridge-listener.preview-mode-blocked.title',
		defaultMessage: 'This content has been updated',
		description:
			'Title for a warning flag shown when the user tries to update page content via Rovo chat while in editor preview mode',
	},
	previewModeBlockedDescription: {
		id: 'rovo-editor-bridge-listener.preview-mode-blocked.description',
		defaultMessage:
			'To view recent changes made via Rovo, select <b>Back</b> or type <code>E</code> to exit preview mode.',
		description:
			'Description for a warning flag shown when the user tries to update page content via Rovo chat while in editor preview mode',
	},
});

export const useEditorRovoBridgeCommandListener = ({
	contentId,
	contentLocation,
	editorAPI,
	setEditorDisabled,
	setTitlePlaceholderState,
	onTitleChange,
	mediaProvider,
	fakeGenerateAIHeaderImage,
	generateAIHeaderImage,
	suggestPageEmoji,
	forceDraftSync,
	flags,
	useGenericEditorSkill,
	onStreamEnd: onStreamEndCallback,
	onSendContextReady,
	additionalContext: additionalContextFromProps,
	onBridgeReady,
	isPreviewMode,
}: UseRovoBridgeCommandListenerParams & WithFlagsProps): void => {
	const contentType = 'page';
	const { sendAnalyticsEvent } = useAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isViewMode = useIsPageViewMode();
	const { isDraftLockedForEditing } = useApprovals();
	const isViewModeForRovo = isViewMode || isDraftLockedForEditing;
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { cloudId, userId } = useSessionData();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { stagingAreaMode } = useStagingAreaState();
	const { trackContentRetention } = useStreamedContentRetentionMetric({ editorAPI, contentId });

	const createMediaProvider: CreateMediaProviderFn = useCallback(async ({ contentId }) => {
		return createRendererMediaProvider({
			contentId,
			isExternalShare: false,
		});
	}, []);

	const onUploadImageError: UploadImageErrorCallback = useCallback(({ error }) => {
		getMonitoringClient().submitError(error, {
			attribution: Attribution.PAGE_MODES,
		});
	}, []);

	const onUploadAndInsertMediaError = useCallback(
		({ retry }: { retry: () => Promise<void> }) => {
			const flagId = 'rovo-bridge-upload-error';
			void flags?.showErrorFlag({
				id: flagId,
				title: intl.formatMessage(i18nMessages.uploadErrorTitle),
				description: intl.formatMessage(i18nMessages.uploadErrorDescription),
				actions: [
					{
						content: intl.formatMessage(i18nMessages.uploadErrorTryAgain),
						onClick: () => {
							void retry()
								.then(() => {
									void flags?.hideFlag(flagId);
								})
								.catch(() => {
									// retry failed — keep the flag visible so the user can try again
								});
						},
					},
				],
			});
		},
		[flags, intl],
	);

	const sendInitialCreationAnalytics = useCallback(async () => {
		try {
			let features;
			if (editorAPI) {
				const doc = await getDocument(editorAPI);
				if (doc) {
					features = extractContentFeatures(doc);
				}
			}

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'initialContentCreated',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: contentType,
					createdContentFeatures: features,
					...(features
						? {
								createdContentAdfLength: features.adfLength,
								createdContentTextLength: features.textLength,
								createdContentWordCount: features.wordCount,
								createdContentIsEmpty: features.textLength === 0 && features.wordCount === 0,
							}
						: {}),
					createdContentMetadata: {
						contentId,
					},
				},
			});
		} catch (error) {
			getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
		}
	}, [editorAPI, contentId, contentType, sendAnalyticsEvent]);

	// Use a ref so that the latest preview mode state is accessible inside callbacks
	const isPreviewModeRef = useRef(isPreviewMode);
	isPreviewModeRef.current = isPreviewMode;

	// Tracks whether quick image generation was already triggered during onStream,
	// preventing duplicate calls if onStream fires multiple times with the title.
	const quickImageGenerationTriggeredRef = useRef(false);

	// Once-per-stream guard for AIFC emoji suggestion (mirrors the header-image ref above).
	const quickEmojiSuggestionTriggeredRef = useRef(false);

	// Called once when streaming starts
	const onStreamStart: StreamStartCallback = useCallback(
		({ streamType, payload }) => {
			if (isPreviewModeRef.current) {
				void flags?.showWarningFlag({
					title: <FormattedMessage {...i18nMessages.previewModeBlockedTitle} />,
					description: (
						<FormattedMessage
							{...i18nMessages.previewModeBlockedDescription}
							values={{
								b: (chunks) => <b>{chunks}</b>,
								code: (chunks) => <b>{chunks}</b>,
							}}
						/>
					),
				});
				return;
			}

			if ((payload?.data as { isRemixGenerated?: boolean })?.isRemixGenerated) {
				editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(true);
			} else {
				setEditorDisabled?.(true);
			}

			if (streamType === 'create' || streamType === 'deep-research-create') {
				setTitlePlaceholderState?.(true);
				// Only show skeleton if real image generation hasn't already been triggered
				if (
					isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img') &&
					!quickImageGenerationTriggeredRef.current
				) {
					void fakeGenerateAIHeaderImage?.();
				}
			}

			// Succeed the AIFC_INITIAL_DATA_RECEIVED experience since content has started streaming.
			// This ensures this experience won't timeout and then fail the AIFC_GENERATE_CONTENT or AIFC_UPDATE_CONTENT experience.
			// NOTE: This experience isn't started for all flows, it'll be a no-op if it's not started.
			void sendAIFCExperienceTrackingToParent({
				status: 'success',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_INITIAL_DATA_RECEIVED,
			});
		},
		[
			editorAPI,
			setEditorDisabled,
			fakeGenerateAIHeaderImage,
			setTitlePlaceholderState,
			contentId,
			contentType,
			contentLocation,
			flags,
		],
	);

	// Called on every stream chunk
	const onStream: StreamCallback = useCallback(
		({ payload, streamType }) => {
			if ('title' in payload.data) {
				const title = payload?.data?.title;
				onTitleChange?.(title);
				if (title) {
					setTitlePlaceholderState?.(false);

					// Trigger header image generation as soon as the title arrives in the
					// stream rather than waiting for the stream to fully complete. The title
					// arrives as a complete string early in the stream, allowing us to start
					// image generation in parallel with body content streaming.
					if (
						(streamType === 'create' || streamType === 'deep-research-create') &&
						isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img') &&
						!quickImageGenerationTriggeredRef.current
					) {
						void generateAIHeaderImage?.({ adfContent: title });
						quickImageGenerationTriggeredRef.current = true;
					}

					// AIFC emoji suggestion, fired in parallel with the header image as soon as a
					// title chunk arrives. Title-only input; BE takes a plain string (not ADF).
					if (
						(streamType === 'create' || streamType === 'deep-research-create') &&
						isExperimentEnabled('cc_page_experiences_aifc_auto_generated_page_emoji') &&
						!quickEmojiSuggestionTriggeredRef.current
					) {
						void suggestPageEmoji?.({ title });
						quickEmojiSuggestionTriggeredRef.current = true;
					}
				}
			}
		},
		[onTitleChange, setTitlePlaceholderState, generateAIHeaderImage, suggestPageEmoji],
	);

	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	// Called when streaming ends
	const onStreamEnd: StreamEndCallback = useCallback(
		async ({ streamType, payload, adfDoc }) => {
			try {
				// reuploadImages is handled in platform

				if (streamType === 'create') {
					const title = 'title' in payload?.data ? payload.data.title : undefined;

					// Fallback: if image generation wasn't already kicked off during onStream
					// (e.g. the title only became available at stream end), trigger it now so
					// the header image is still generated.
					if (
						title &&
						isExperimentEnabled('cc_page_experiences_aifc_auto_generated_header_img') &&
						!quickImageGenerationTriggeredRef.current
					) {
						void generateAIHeaderImage?.({ adfContent: title });
						quickImageGenerationTriggeredRef.current = true;
					}

					// Stream-end fallback: title only materialised here.
					if (
						title &&
						isExperimentEnabled('cc_page_experiences_aifc_auto_generated_page_emoji') &&
						!quickEmojiSuggestionTriggeredRef.current
					) {
						void suggestPageEmoji?.({ title });
						quickEmojiSuggestionTriggeredRef.current = true;
					}
				}

				// This experience success is a no-op if AIFC_ALL_DATA_HANDLED is not started. It may not be started for all flows.
				void sendAIFCExperienceTrackingToParent({
					status: 'success',
					actionType: contentType,
					actionId: contentId,
					contentContext: contentLocation,
					experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				});

				if (streamType === 'create' || streamType === 'deep-research-create') {
					void sendInitialCreationAnalytics();

					if (streamType === 'create') {
						const singleInstrumentationId =
							typeof payload?.singleInstrumentationId === 'string'
								? payload.singleInstrumentationId
								: undefined;
						void trackContentRetention({ singleInstrumentationId, adfDoc });
					}
				} else if (streamType === 'update') {
					if (!showSmartCreationModal && stagingAreaMode !== 'modal') {
						const traceId = typeof payload?.traceId === 'string' ? payload.traceId : undefined;
						const requestId =
							typeof payload?.requestId === 'string' ? payload.requestId : undefined;
						void showCreateWithRovoModalCloseFeedbackFlag({
							feedbackVariant: FeedbackVariant.AFTER_UPDATE_STREAM_COMPLETE,
							contentType,
							cloudId,
							traceId,
							requestId,
						});
					}
				}
			} finally {
				setEditorDisabled?.(false);
				editorAPI?.aiExperience?.actions.toggleAiEntryPoints?.(false);
			}

			onStreamEndCallback?.();
		},
		[
			editorAPI,
			contentId,
			contentType,
			contentLocation,
			setEditorDisabled,
			generateAIHeaderImage,
			suggestPageEmoji,
			sendInitialCreationAnalytics,
			showCreateWithRovoModalCloseFeedbackFlag,
			showSmartCreationModal,
			stagingAreaMode,
			cloudId,
			onStreamEndCallback,
			trackContentRetention,
		],
	);

	const onStreamAbort: StreamAbortCallback = useCallback(() => {
		setEditorDisabled?.(false);

		sendAnalyticsEvent({
			eventType: 'operational',
			action: 'aborted',
			actionSubject: 'smartCreateContent',
			attributes: {
				contentType,
				contentId,
			},
		});
	}, [setEditorDisabled, contentId, contentType, sendAnalyticsEvent]);

	const onStreamTimeout: StreamTimeoutCallback = useCallback(
		({ streamType, traceId }) => {
			setEditorDisabled?.(false);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'timeout',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType,
					contentId,
					streamType,
					traceId,
				},
			});
		},
		[setEditorDisabled, contentId, contentType, sendAnalyticsEvent],
	);

	const onStreamError: StreamErrorCallback = useCallback(
		({ error, streamType, traceId, streamDebug }) => {
			log('Error in command handler:', error);

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'error',
				actionSubject: 'smartCreateContent',
				attributes: {
					contentType,
					error: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error && error.stack ? error.stack : undefined,
					streamType,
					traceId,
					contentId,
					// Shape-only debug metadata for blank-page create investigations.
					// Only populated when the bridge feature gate is enabled; the
					// object never contains UGC/raw content (see CreateStreamDebug).
					...(streamDebug ? { streamDebug } : {}),
				},
			});

			void sendAIFCExperienceTrackingToParent({
				status: 'fail',
				actionType: contentType,
				actionId: contentId,
				contentContext: contentLocation,
				experienceName: RovoExperienceName.AIFC_ALL_DATA_HANDLED,
				error: error instanceof Error ? error.message : String(error),
			});

			if (fg('aifc_create_enabled')) {
				setEditorDisabled?.(false);
			}
		},
		[contentId, contentType, contentLocation, sendAnalyticsEvent, setEditorDisabled],
	);

	const onStreamRepair: StreamRepairCallback = useCallback(
		({ streamType, repairs }) => {
			sendAnalyticsEvent({
				eventType: 'operational',
				actionSubject: 'smartCreateContent',
				action: 'adfRepaired',
				attributes: {
					contentType,
					contentId,
					streamType,
					repairCount: repairs.length,
					// Manually selecting properties to avoid sending UGC
					// in case more properties are added in the future.
					repairs: repairs.map((repair) => ({
						type: repair.type,
						from: repair.from,
						to: repair.to,
						context: repair.context,
						expansion: repair.expansion,
					})),
				},
			});
		},
		[contentId, contentType, sendAnalyticsEvent],
	);

	const additionalContext = useMemo<AdditionalContext>(() => {
		return async () => {
			const more =
				typeof additionalContextFromProps === 'function'
					? await additionalContextFromProps()
					: additionalContextFromProps;
			return {
				...more,
				isViewMode: isViewModeForRovo,
				isDraftLockedForEditing,
			};
		};
	}, [isViewModeForRovo, isDraftLockedForEditing, additionalContextFromProps]);

	const addCommandHandlers: AddCommandHandlersFn = useCallback(
		({ commandManager }) => {
			commandManager
				.addDefaultCommandHandlers()
				.addCommandHandler(
					ConfluenceDeepResearchPageCreateCommand,
					new DeepResearchCommandHandler(commandManager.commandProps),
				)
				.addCommandHandler(
					ConfluencePageInitCommand,
					new PageInitCommandHandler(commandManager.commandProps),
				)
				.addCommandHandler(
					PrepareContentForFinalizationCommand,
					new PrepareContentForFinalizationCommandHandler(commandManager.commandProps, {
						forceDraftSync,
					}),
				)
				.addCommandHandler(
					ConfluenceSubmitPromptOnPageCreateCommand,
					new SubmitPromptCommandHandler(commandManager.commandProps),
				);
		},
		[forceDraftSync],
	);

	useEditorRovoBridge({
		userId: userId ?? undefined,
		contentLocation,
		contentId,
		editorAPI,
		addCommandHandlers,
		additionalContext,
		mediaProvider,
		createMediaProvider,
		onUploadImageError,
		onUploadAndInsertMediaError,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamAbort,
		onStreamTimeout,
		onStreamError,
		onStreamRepair,
		onSendContextReady,
		useGenericEditorSkill,
		onBridgeReady,
	});
};
